import type React from 'react';
import type { ResourceDetailFolder } from '@fabric-app/types/resources';
import {
  useContextShareToken,
  type PermissionData,
} from '@fabric-app/business/permission/contexts/AnonShareTokenContext';
import { isResourceRootFolder } from '@fabric-app/business/resources/utils/isResourceRootFolder';
import { useAnalytics } from '@fabric-app/business/analytics/hooks/useAnalytics';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { AnalyticsEvents } from '@fabric-app/business/analytics/analytics.types';
import ExpandedFolder from '../../../../views/ExpandedFolder/ExpandedFolder';
import { ExpandedSpace } from '../../../../views/ExpandedSpace';
import { useRouter } from 'next/router';
import { getRouteParamAsString } from '@fabric-app/utils/url/routing';
import { omit } from '@fabric-app/utils/object/manipulation';

/**
 * Public directory is either a space or space (sub)folder
 * decides whether to show Space or Folder view
 */
export const PublishedSpace: React.FC<{
  /**
   * can be a space root folder or just a folder
   * marks the root where user can't navigate back via [...]
   */
  rootFolder: ResourceDetailFolder;
}> = ({ rootFolder }) => {
  const token = useContextShareToken() as PermissionData;
  const router = useRouter();

  const [activeFolderId] = getRouteParamAsString(router.query, ['fid']);

  const isFolderSpaceRoot = isResourceRootFolder(rootFolder);

  const { track } = useAnalytics();

  useRunOnce(() => {
    track(AnalyticsEvents.VisitedSharedLink, {
      ...(isFolderSpaceRoot
        ? { type: 'space', spaceId: token.resourceId }
        : { type: 'folder', folderId: rootFolder.id }),
      ownerId: rootFolder.user?.id,
      role: token.role,
    });
  });

  const onCloseFolder = () => {
    router.push({
      pathname: router.pathname,
      query: omit(router.query, ['fid']),
    });
  };

  const onClickFolder = (resourceFolder: ResourceDetailFolder | string) => {
    const nextFolderId = typeof resourceFolder === 'string' ? resourceFolder : resourceFolder.id;

    if (nextFolderId === rootFolder.id) {
      /**
       * do not include the root folder id in the query
       */
      router.push({
        pathname: router.pathname,
        query: omit(router.query, ['fid']),
      });
    } else {
      router.push({
        pathname: router.pathname,
        query: {
          ...router.query,
          fid: nextFolderId,
        },
      });
    }
  };

  return (
    <>
      {activeFolderId ? (
        <ExpandedFolder
          key={activeFolderId}
          overrideOnClose={onCloseFolder}
          resourceId={activeFolderId}
          onClickFolder={onClickFolder}
        />
      ) : isFolderSpaceRoot ? (
        <ExpandedSpace
          resourceId={token.resourceId}
          overrideOnClose={null}
          onClickFolder={onClickFolder}
        />
      ) : (
        <ExpandedFolder
          key={rootFolder.id}
          overrideOnClose={null}
          resourceId={rootFolder.id}
          onClickFolder={onClickFolder}
        />
      )}
    </>
  );
};
